// @flow
import React, { useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import Tippy from '@tippyjs/react';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock-upgrade';
import { t } from 'javascripts/utils/withTranslation';
import ErrorBoundary from 'javascripts/utils/ErrorBoundary';
import randomString from 'javascripts/utils/random-id';
import classNames from 'classnames';
import SearchField from 'components/organisms/search/react/Search';
import Button from 'components/atoms/button/Button';
import type { ModalProps } from '../types';
import Icon from '../../../_particles/icon/react/Icon';
import SearchFallBack from './SearchFallback';
import useMediaquery from '../../../../javascripts/hooks/useMediaquery';
import { MEDIA_QUERIES } from '../../../../javascripts/constants';

const Search = ({
  isOpen,
  setIsOpen,
  searchUrl,
  suggestUrl,
  searchTooltip,
  suggestionItems,
  suggestFieldName,
  searchFieldName,
  searchFieldPlaceholder,
  searchFieldLabel,
  searchSuggestTitle,
  ...props
}: ModalProps): React$MixedElement => {
  const isDesktop = useMediaquery(MEDIA_QUERIES.xxl);
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const modifier = randomString();

  useEffect(() => {
    if (isOpen) {
      setTooltipIsOpen(false);
    }
  }, [isOpen]);

  return (
    <>
      <Tippy
        visible={tooltipIsOpen}
        content={searchTooltip || t('Suche')}
        placement="bottom"
        animation="shift-away"
        arrow={false}
        theme="bw"
      >
        <button
          type="button"
          className="header__icon"
          aria-expanded={isOpen}
          aria-labelledby={`header-icon-label--search--${modifier}`}
          onClick={() => setIsOpen(true)}
          onMouseEnter={() => setTooltipIsOpen(!isOpen)}
          onFocus={() => setTooltipIsOpen(!isOpen)}
          onMouseLeave={() => setTooltipIsOpen(false)}
          onBlur={() => setTooltipIsOpen(false)}
        >
          <Icon icon="search" ariaHidden={true} focusable={false} />

          <span
            id={`header-icon-label--search--${modifier}`}
            className="header__icon-text"
            hidden
          >
            {t('Suche')}
          </span>
        </button>
      </Tippy>

      <ReactModal
        isOpen={isOpen}
        onRequestClose={() => setIsOpen(false)}
        role="dialog"
        contentLabel={t('Suche')}
        overlayClassName={classNames(
          'modal__overlay modal__overlay--search',
          props.showBurgerDesktop && 'modal__overlay--has-burger',
        )}
        className="modal"
        id={`header-icon-content--search--${modifier}`}
        aria={{
          labelledby: `header-icon-label--search--${modifier}`,
        }}
      >
        <section className="modal__section">
          <ErrorBoundary>
            <div className="modal__header">
              <div className="header__container">
                <Button
                  icon="close"
                  iconTitle={t('Schließen')}
                  className="modal__close"
                  style="chromeless"
                  onClick={() => setIsOpen(false)}
                />
              </div>
            </div>
            <div
              className="modal__content"
              ref={(node) => {
                if (isOpen && !isDesktop && node) {
                  disableBodyScroll(node);
                } else if (node) {
                  enableBodyScroll(node);
                }
              }}
            >
              {suggestUrl ? (
                <SearchField
                  name={searchFieldName || 'tx_solr[q]'}
                  placeholder={
                    searchFieldPlaceholder || t('Suchbegriff eingeben')
                  }
                  label={searchFieldLabel || t('Wonach suchen Sie?')}
                  suggestionItems={suggestionItems || []}
                  formProps={{ action: searchUrl, method: 'get' }}
                  suggestUrl={suggestUrl}
                  suggestFieldName={suggestFieldName || 'tx_solr[queryString]'}
                  ref={(node) => node?.focus()}
                  suggestTitle={searchSuggestTitle || t('Top-Themen')}
                />
              ) : (
                t(
                  'Die Suche konnte nicht geladen werden. Bitte versuchen Sie es später erneut.',
                )
              )}
            </div>
          </ErrorBoundary>
        </section>
      </ReactModal>
    </>
  );
};

export default Search;
